import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Download, Calendar, Filter, TrendingUp, Users, Package, Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { exportService } from '../../services/exportService';
import { useAppStore } from '../../stores/appStore';

interface ReportConfig {
  id: string;
  name: string;
  description: string;
  type: 'performance' | 'inventory' | 'quality' | 'delivery' | 'financial';
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
  format: 'excel' | 'pdf' | 'csv' | 'json';
  recipients: string[];
  filters: ReportFilter[];
  metrics: string[];
  isActive: boolean;
}

interface ReportFilter {
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'between' | 'in';
  value: any;
  label: string;
}

interface ReportMetric {
  id: string;
  name: string;
  description: string;
  category: string;
  dataType: 'number' | 'percentage' | 'currency' | 'duration';
  aggregation: 'sum' | 'average' | 'count' | 'min' | 'max';
}

export const AdvancedReporting: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportConfigs, setReportConfigs] = useState<ReportConfig[]>([]);
  const { addNotification } = useAppStore();

  useEffect(() => {
    initializeReportConfigs();
  }, []);

  const initializeReportConfigs = () => {
    const configs: ReportConfig[] = [
      {
        id: 'performance_summary',
        name: 'Performance Summary',
        description: 'Comprehensive performance metrics and KPIs',
        type: 'performance',
        frequency: 'weekly',
        format: 'excel',
        recipients: ['manager@warehouse.com'],
        filters: [],
        metrics: ['pick_rate', 'accuracy', 'efficiency', 'order_cycle_time'],
        isActive: true
      },
      {
        id: 'inventory_analysis',
        name: 'Inventory Analysis',
        description: 'Stock levels, turnover, and movement analysis',
        type: 'inventory',
        frequency: 'daily',
        format: 'pdf',
        recipients: ['inventory@warehouse.com'],
        filters: [
          {
            field: 'category',
            operator: 'in',
            value: ['dairy', 'produce', 'frozen'],
            label: 'Product Categories'
          }
        ],
        metrics: ['stock_levels', 'turnover_rate', 'dead_stock', 'reorder_points'],
        isActive: true
      },
      {
        id: 'quality_metrics',
        name: 'Quality Metrics',
        description: 'Quality control and compliance metrics',
        type: 'quality',
        frequency: 'monthly',
        format: 'excel',
        recipients: ['quality@warehouse.com'],
        filters: [],
        metrics: ['damage_rate', 'expiry_compliance', 'temperature_violations', 'customer_complaints'],
        isActive: true
      },
      {
        id: 'delivery_performance',
        name: 'Delivery Performance',
        description: 'Delivery times, accuracy, and customer satisfaction',
        type: 'delivery',
        frequency: 'weekly',
        format: 'pdf',
        recipients: ['logistics@warehouse.com'],
        filters: [],
        metrics: ['on_time_delivery', 'delivery_accuracy', 'customer_satisfaction', 'route_efficiency'],
        isActive: true
      }
    ];

    setReportConfigs(configs);
  };

  const availableMetrics: ReportMetric[] = [
    {
      id: 'pick_rate',
      name: 'Pick Rate',
      description: 'Items picked per hour',
      category: 'Performance',
      dataType: 'number',
      aggregation: 'average'
    },
    {
      id: 'accuracy',
      name: 'Pick Accuracy',
      description: 'Percentage of correct picks',
      category: 'Performance',
      dataType: 'percentage',
      aggregation: 'average'
    },
    {
      id: 'efficiency',
      name: 'Efficiency',
      description: 'Overall operational efficiency',
      category: 'Performance',
      dataType: 'percentage',
      aggregation: 'average'
    },
    {
      id: 'order_cycle_time',
      name: 'Order Cycle Time',
      description: 'Time from order to completion',
      category: 'Performance',
      dataType: 'duration',
      aggregation: 'average'
    },
    {
      id: 'stock_levels',
      name: 'Stock Levels',
      description: 'Current inventory quantities',
      category: 'Inventory',
      dataType: 'number',
      aggregation: 'sum'
    },
    {
      id: 'turnover_rate',
      name: 'Inventory Turnover',
      description: 'Rate of inventory movement',
      category: 'Inventory',
      dataType: 'number',
      aggregation: 'average'
    },
    {
      id: 'damage_rate',
      name: 'Damage Rate',
      description: 'Percentage of damaged items',
      category: 'Quality',
      dataType: 'percentage',
      aggregation: 'average'
    },
    {
      id: 'on_time_delivery',
      name: 'On-Time Delivery',
      description: 'Percentage of on-time deliveries',
      category: 'Delivery',
      dataType: 'percentage',
      aggregation: 'average'
    }
  ];

  const handleGenerateReport = async (reportId: string) => {
    setIsGenerating(true);
    
    try {
      const config = reportConfigs.find(c => c.id === reportId);
      if (!config) throw new Error('Report configuration not found');

      // Generate mock data for the report
      const reportData = {
        pickLists: [], // Mock data would be populated here
        pickItems: [],
        metrics: {
          totalPicksToday: 156,
          averagePickTime: 45,
          accuracy: 97.8,
          efficiency: 89.2,
          completedOrders: 23,
          pendingOrders: 7
        },
        metadata: {
          exportedAt: new Date().toISOString(),
          exportedBy: 'current-user',
          filters: {
            dateRange,
            reportType: config.type
          }
        }
      };

      let blob: Blob;
      let filename: string;

      switch (config.format) {
        case 'excel':
          blob = await exportService.exportToExcel(reportData);
          filename = `${config.name.replace(/\s+/g, '_')}_${dateRange.start}_to_${dateRange.end}.xlsx`;
          break;
        case 'pdf':
          blob = await exportService.exportToPDF(reportData);
          filename = `${config.name.replace(/\s+/g, '_')}_${dateRange.start}_to_${dateRange.end}.pdf`;
          break;
        case 'csv':
          blob = await exportService.exportToCSV(reportData);
          filename = `${config.name.replace(/\s+/g, '_')}_${dateRange.start}_to_${dateRange.end}.csv`;
          break;
        case 'json':
          blob = await exportService.exportToJSON(reportData);
          filename = `${config.name.replace(/\s+/g, '_')}_${dateRange.start}_to_${dateRange.end}.json`;
          break;
        default:
          throw new Error('Unsupported format');
      }

      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addNotification({
        type: 'success',
        title: 'Report Generated',
        message: `${config.name} has been generated and downloaded`,
        priority: 'medium'
      });

    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Report Generation Failed',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        priority: 'high'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getReportIcon = (type: string) => {
    switch (type) {
      case 'performance':
        return <TrendingUp className="w-5 h-5" />;
      case 'inventory':
        return <Package className="w-5 h-5" />;
      case 'quality':
        return <BarChart3 className="w-5 h-5" />;
      case 'delivery':
        return <Clock className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'performance':
        return 'text-blue-600 bg-blue-100';
      case 'inventory':
        return 'text-green-600 bg-green-100';
      case 'quality':
        return 'text-purple-600 bg-purple-100';
      case 'delivery':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Advanced Reporting</h2>
          <p className="text-gray-600">Generate comprehensive reports and analytics</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      {/* Report Configurations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {reportConfigs.map((config) => (
          <motion.div
            key={config.id}
            whileHover={{ scale: 1.02 }}
            className="cursor-pointer"
          >
            <Card className={`${selectedReport === config.id ? 'ring-2 ring-blue-500' : ''}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(config.type)}`}>
                    {getReportIcon(config.type)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{config.name}</h3>
                    <p className="text-sm text-gray-600">{config.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Badge variant={config.isActive ? 'success' : 'neutral'}>
                    {config.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium capitalize">{config.type}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Frequency:</span>
                  <span className="font-medium capitalize">{config.frequency}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Format:</span>
                  <span className="font-medium uppercase">{config.format}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Metrics:</span>
                  <span className="font-medium">{config.metrics.length} selected</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleGenerateReport(config.id)}
                    loading={isGenerating}
                    disabled={!config.isActive}
                    className="flex-1"
                    icon={<Download className="w-4 h-4" />}
                  >
                    Generate Report
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedReport(selectedReport === config.id ? null : config.id)}
                  >
                    {selectedReport === config.id ? 'Hide' : 'Details'}
                  </Button>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedReport === config.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t border-gray-200"
                >
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Included Metrics</h4>
                      <div className="flex flex-wrap gap-2">
                        {config.metrics.map(metricId => {
                          const metric = availableMetrics.find(m => m.id === metricId);
                          return metric ? (
                            <Badge key={metricId} variant="neutral" size="sm">
                              {metric.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                    
                    {config.filters.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Applied Filters</h4>
                        <div className="space-y-2">
                          {config.filters.map((filter, index) => (
                            <div key={index} className="text-sm text-gray-600">
                              <span className="font-medium">{filter.label}:</span> {filter.operator} {Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Recipients</h4>
                      <div className="flex flex-wrap gap-2">
                        {config.recipients.map(email => (
                          <Badge key={email} variant="neutral" size="sm">
                            {email}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Export</h3>
        <p className="text-gray-600 mb-4">
          Generate instant reports with current data
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button
            variant="outline"
            onClick={() => handleGenerateReport('performance_summary')}
            loading={isGenerating}
            className="justify-start"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Performance
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleGenerateReport('inventory_analysis')}
            loading={isGenerating}
            className="justify-start"
          >
            <Package className="w-4 h-4 mr-2" />
            Inventory
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleGenerateReport('quality_metrics')}
            loading={isGenerating}
            className="justify-start"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Quality
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleGenerateReport('delivery_performance')}
            loading={isGenerating}
            className="justify-start"
          >
            <Clock className="w-4 h-4 mr-2" />
            Delivery
          </Button>
        </div>
      </Card>

      {/* Available Metrics */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Metrics</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableMetrics.map(metric => (
            <div key={metric.id} className="p-3 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{metric.name}</h4>
                <Badge variant="neutral" size="sm">
                  {metric.category}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 mb-2">{metric.description}</p>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <span>Type: {metric.dataType}</span>
                <span>•</span>
                <span>Agg: {metric.aggregation}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};